import React, { useEffect, useState, useCallback } from "react";
import { authFetch, C } from "./App";
import { GHANA_REGIONS, GHANA_REGIONS_DISTRICTS } from "./ghanaRegionsDistricts";

export function DistrictsView({ token }) {
  const [districts, setDistricts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [editing, setEditing] = useState(null); // district object or "new"
  const [actingId, setActingId] = useState(null);

  const load = useCallback(() => {
    setLoading(true);
    authFetch("/admin/districts", token)
      .then((d) => setDistricts(d.districts || []))
      .catch((e) => setErr(e.message))
      .finally(() => setLoading(false));
  }, [token]);

  useEffect(() => { load(); }, [load]);

  function toggleActive(d) {
    setActingId(d.id);
    authFetch(`/admin/districts/${d.id}`, token, {
      method: "PATCH",
      body: JSON.stringify({ is_active: !d.is_active }),
    })
      .then(load)
      .catch((e) => setErr(e.message))
      .finally(() => setActingId(null));
  }

  function remove(id, name) {
    if (!window.confirm(`Delete "${name}"? This removes it from any public URL immediately.`)) return;
    authFetch(`/admin/districts/${id}`, token, { method: "DELETE" })
      .then(load)
      .catch((e) => setErr(e.message));
  }

  return (
    <div>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 4 }}>
        <h1 style={{ fontSize: 22, fontWeight: 700, color: "#111", margin: 0 }}>Districts</h1>
        <button onClick={() => setEditing("new")} style={{ background: C.green, color: "#fff", border: "none", borderRadius: 8, padding: "9px 16px", fontSize: 13, fontWeight: 600, cursor: "pointer" }}>+ Add District</button>
      </div>
      <p style={{ fontSize: 13, color: C.gray, margin: "0 0 20px" }}>Onboard a district to go live at mysafeghana.com/region/district with its own filtered view.</p>

      {err && <div style={{ color: C.danger, fontSize: 13, marginBottom: 14 }}>{err}</div>}

      {loading ? (
        <div style={{ color: C.gray, fontSize: 13 }}>Loading…</div>
      ) : districts.length === 0 ? (
        <div style={{ color: C.gray, fontSize: 13 }}>No districts yet. Click "+ Add District" to onboard your first client.</div>
      ) : (
        <div style={{ background: C.white, border: `1px solid ${C.border}`, borderRadius: 12, overflow: "hidden" }}>
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
            <thead>
              <tr style={{ background: C.grayLight, textAlign: "left" }}>
                <Th>Region</Th><Th>District</Th><Th>URL</Th><Th>Status</Th><Th>Added</Th><Th></Th>
              </tr>
            </thead>
            <tbody>
              {districts.map((d) => (
                <tr key={d.id} style={{ borderTop: `1px solid ${C.border}` }}>
                  <Td>{d.region}</Td>
                  <Td><b>{d.district}</b></Td>
                  <Td style={{ color: C.gray, fontFamily: "monospace", fontSize: 12 }}>/{d.region_slug}/{d.district_slug}</Td>
                  <Td>
                    <button
                      onClick={() => toggleActive(d)}
                      disabled={actingId === d.id}
                      style={{
                        fontSize: 10.5, fontWeight: 600, border: "none", borderRadius: 8, padding: "3px 9px", cursor: actingId === d.id ? "default" : "pointer",
                        color: d.is_active ? "#2E7D32" : "#777",
                        background: d.is_active ? "#E8F5E9" : C.grayLight,
                        opacity: actingId === d.id ? 0.5 : 1,
                      }}
                      title="Click to toggle live status"
                    >
                      {d.is_active ? "LIVE" : "DRAFT"}
                    </button>
                  </Td>
                  <Td style={{ color: C.gray }}>{new Date(d.created_at).toLocaleDateString()}</Td>
                  <Td>
                    <div style={{ display: "flex", gap: 6 }}>
                      <button onClick={() => setEditing(d)} style={{ background: "transparent", border: `1px solid ${C.border}`, borderRadius: 7, padding: "5px 10px", fontSize: 11.5, fontWeight: 600, cursor: "pointer" }}>Edit</button>
                      <button onClick={() => remove(d.id, d.display_name)} style={{ background: "transparent", border: "1px solid #C6282855", color: "#C62828", borderRadius: 7, padding: "5px 10px", fontSize: 11.5, fontWeight: 600, cursor: "pointer" }}>Delete</button>
                    </div>
                  </Td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {editing && (
        <DistrictEditModal
          token={token} district={editing === "new" ? null : editing}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); load(); }}
        />
      )}
    </div>
  );
}

function Th({ children }) { return <th style={{ padding: "10px 14px", fontSize: 11, fontWeight: 700, color: C.gray, textTransform: "uppercase", letterSpacing: 0.5 }}>{children}</th>; }
function Td({ children, style }) { return <td style={{ padding: "10px 14px", ...style }}>{children}</td>; }

function slugify(str) {
  return String(str || "")
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, "")
    .replace(/^-+|-+$/g, "");
}

function DistrictEditModal({ token, district, onClose, onSaved }) {
  const [form, setForm] = useState({
    region: district?.region || "",
    district: district?.district || "",
    region_slug: district?.region_slug || "",
    district_slug: district?.district_slug || "",
    map_center_lat: district?.map_center_lat || "",
    map_center_lng: district?.map_center_lng || "",
    map_zoom: district?.map_zoom || 11,
    logo_url: district?.logo_url || "",
    accent_color: district?.accent_color || "",
    contact_name: district?.contact_name || "",
    contact_email: district?.contact_email || "",
    contact_phone: district?.contact_phone || "",
    is_active: district?.is_active || false,
  });
  const [slugTouched, setSlugTouched] = useState(!!district); // don't auto-slug on edit unless user clears it
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const [boundaryText, setBoundaryText] = useState(
    district?.boundary_geojson ? JSON.stringify(district.boundary_geojson, null, 2) : ""
  );
  const [boundaryErr, setBoundaryErr] = useState("");
  const [boundaryInfo, setBoundaryInfo] = useState("");

  // Auto-populated boundary from the Ghana Statistical Service reference
  // library (see api's scripts/refresh-district-boundary-references.js).
  // Only kicks in when there's no boundary already on this district (fresh
  // "Add District", or editing one that was created without one, like
  // Ayawaso West) — never overwrites a boundary someone already set.
  const [refBoundary, setRefBoundary] = useState(null); // { boundary_geojson, source, matched_shape_name } | null
  const [refLoading, setRefLoading] = useState(false);
  const [manualOverride, setManualOverride] = useState(!!boundaryText);

  useEffect(() => {
    if (manualOverride || boundaryText.trim() || !form.region || !form.district) {
      setRefBoundary(null);
      return;
    }
    let cancelled = false;
    setRefLoading(true);
    authFetch(`/admin/districts/boundary-reference?region=${encodeURIComponent(form.region)}&district=${encodeURIComponent(form.district)}`, token)
      .then((d) => { if (!cancelled) setRefBoundary(d.found ? d : null); })
      .catch(() => { if (!cancelled) setRefBoundary(null); })
      .finally(() => { if (!cancelled) setRefLoading(false); });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.region, form.district, manualOverride, token]);

  function onBoundaryChange(text) {
    setBoundaryText(text);
    setBoundaryErr("");
    setBoundaryInfo("");
    if (!text.trim()) return;
    let parsed;
    try { parsed = JSON.parse(text); }
    catch (e) { setBoundaryErr("Not valid JSON: " + e.message); return; }
    if (!parsed || typeof parsed !== "object" || (parsed.type !== "Polygon" && parsed.type !== "MultiPolygon") || !Array.isArray(parsed.coordinates)) {
      setBoundaryErr('Must be a bare Polygon or MultiPolygon geometry object — e.g. {"type":"Polygon","coordinates":[...]}. Not a Feature or FeatureCollection.');
      return;
    }
    const ringCount = parsed.type === "Polygon" ? parsed.coordinates.length : parsed.coordinates.flat().length;
    setBoundaryInfo(`Looks valid — ${parsed.type}, ${ringCount} ring${ringCount === 1 ? "" : "s"}.`);
  }

  function update(field, value) {
    setForm((f) => {
      const next = { ...f, [field]: value };
      // Auto-generate slugs from names unless the user has manually edited a slug field
      if (!slugTouched) {
        if (field === "region") next.region_slug = slugify(value);
        if (field === "district") next.district_slug = slugify(value);
      }
      return next;
    });
  }

  function submit(e) {
    e.preventDefault();
    if (!form.region.trim() || !form.district.trim()) return;
    if (boundaryErr) return; // fix the pasted GeoJSON first

    let boundary_geojson;
    if (refBoundary && !manualOverride) {
      boundary_geojson = refBoundary.boundary_geojson;
    } else if (boundaryText.trim()) {
      try { boundary_geojson = JSON.parse(boundaryText); }
      catch (e) { setBoundaryErr("Not valid JSON: " + e.message); return; }
    }

    setBusy(true); setErr("");
    const body = JSON.stringify({
      ...form,
      map_center_lat: form.map_center_lat === "" ? null : Number(form.map_center_lat),
      map_center_lng: form.map_center_lng === "" ? null : Number(form.map_center_lng),
      map_zoom: form.map_zoom === "" ? 11 : Number(form.map_zoom),
      // omit boundary_geojson entirely when the textarea is empty — leaves an
      // existing boundary untouched on edit, rather than silently clearing it
      ...(boundary_geojson !== undefined ? { boundary_geojson } : {}),
    });
    const call = district
      ? authFetch(`/admin/districts/${district.id}`, token, { method: "PATCH", body })
      : authFetch("/admin/districts", token, { method: "POST", body });
    call.then(onSaved).catch((e) => setErr(e.message)).finally(() => setBusy(false));
  }

  return (
    <div onClick={onClose} style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.4)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 100, padding: 16 }}>
      <form onClick={(e) => e.stopPropagation()} onSubmit={submit} style={{ background: "#fff", borderRadius: 14, padding: 24, width: 460, maxHeight: "88vh", overflowY: "auto", boxShadow: "0 20px 60px rgba(0,0,0,0.3)" }}>
        <div style={{ fontSize: 16, fontWeight: 700, marginBottom: 16 }}>{district ? "Edit District" : "Add District"}</div>
        {err && <div style={{ color: C.danger, fontSize: 12.5, marginBottom: 12 }}>{err}</div>}

        <Row>
          <Field label="Region" required>
            <select
              value={form.region}
              onChange={(e) => { update("region", e.target.value); setForm((f) => ({ ...f, district: "" })); }}
              required style={inputStyle}
            >
              <option value="">— Select region —</option>
              {GHANA_REGIONS.map((r) => <option key={r} value={r}>{r}</option>)}
            </select>
          </Field>
          <Field label="District" required>
            <select
              value={form.district}
              onChange={(e) => update("district", e.target.value)}
              required disabled={!form.region} style={inputStyle}
            >
              <option value="">{form.region ? "— Select district —" : "Select a region first"}</option>
              {(GHANA_REGIONS_DISTRICTS[form.region] || []).map((d) => <option key={d} value={d}>{d}</option>)}
            </select>
          </Field>
        </Row>

        <Row>
          <Field label="URL: Region Slug">
            <input
              value={form.region_slug}
              onChange={(e) => { setSlugTouched(true); setForm((f) => ({ ...f, region_slug: slugify(e.target.value) })); }}
              style={{ ...inputStyle, fontFamily: "monospace", fontSize: 12.5 }}
            />
          </Field>
          <Field label="URL: District Slug">
            <input
              value={form.district_slug}
              onChange={(e) => { setSlugTouched(true); setForm((f) => ({ ...f, district_slug: slugify(e.target.value) })); }}
              style={{ ...inputStyle, fontFamily: "monospace", fontSize: 12.5 }}
            />
          </Field>
        </Row>
        <div style={{ fontSize: 11, color: C.gray, marginTop: -6, marginBottom: 14 }}>
          Live URL preview: <span style={{ fontFamily: "monospace" }}>mysafeghana.com/{form.region_slug || "…"}/{form.district_slug || "…"}</span>
        </div>

        <Row>
          <Field label="Map Center Lat">
            <input type="number" step="any" value={form.map_center_lat} onChange={(e) => update("map_center_lat", e.target.value)} style={inputStyle} />
          </Field>
          <Field label="Map Center Lng">
            <input type="number" step="any" value={form.map_center_lng} onChange={(e) => update("map_center_lng", e.target.value)} style={inputStyle} />
          </Field>
          <Field label="Zoom">
            <input type="number" value={form.map_zoom} onChange={(e) => update("map_zoom", e.target.value)} style={inputStyle} />
          </Field>
        </Row>

        <Field label="Logo URL (optional — leave blank for standard branding)">
          <input value={form.logo_url} onChange={(e) => update("logo_url", e.target.value)} style={inputStyle} placeholder="https://…" />
        </Field>

        <Field label="Accent Color (optional)">
          <input value={form.accent_color} onChange={(e) => update("accent_color", e.target.value)} style={inputStyle} placeholder="#006B3F" />
        </Field>

        <Field label="Boundary GeoJSON (optional)">
          {refLoading && <div style={{ fontSize: 11, color: C.gray, marginBottom: 6 }}>Checking for a reference boundary…</div>}
          {refBoundary && !manualOverride ? (
            <div style={{ border: `1px solid ${C.border}`, borderRadius: 8, padding: 10, background: "#F3F8F5" }}>
              <div style={{ fontSize: 12.5, color: C.success, fontWeight: 600 }}>✓ Boundary auto-loaded from Ghana Statistical Service data</div>
              <div style={{ fontSize: 11, color: C.gray, marginTop: 3 }}>
                Matched "{refBoundary.matched_shape_name}" — {refBoundary.boundary_geojson.type}
              </div>
              <button
                type="button"
                onClick={() => { setManualOverride(true); setBoundaryText(JSON.stringify(refBoundary.boundary_geojson, null, 2)); }}
                style={{ marginTop: 6, background: "none", border: "none", color: C.gray, textDecoration: "underline", fontSize: 11, cursor: "pointer", padding: 0 }}
              >
                Override manually
              </button>
            </div>
          ) : (
            <>
              <textarea
                value={boundaryText}
                onChange={(e) => onBoundaryChange(e.target.value)}
                rows={5}
                placeholder='{"type":"Polygon","coordinates":[[[lng,lat],[lng,lat],...]]}'
                style={{ ...inputStyle, fontFamily: "monospace", fontSize: 11.5, resize: "vertical" }}
              />
              <div style={{ fontSize: 10.5, color: C.gray, marginTop: 4 }}>
                Draws the district outline on the citizen map and clips its scoped view. Paste a bare
                Polygon or MultiPolygon geometry object (not a Feature/FeatureCollection). Leave blank
                to keep this district's existing boundary unchanged.
                {!refLoading && form.region && form.district && !manualOverride && (
                  " No reference boundary was found for this region/district — it isn't in the Ghana Statistical Service dataset yet."
                )}
              </div>
              {boundaryErr && <div style={{ fontSize: 11, color: C.danger, marginTop: 4 }}>{boundaryErr}</div>}
              {boundaryInfo && <div style={{ fontSize: 11, color: C.success, marginTop: 4 }}>{boundaryInfo}</div>}
            </>
          )}
        </Field>

        <Row>
          <Field label="Contact Name">
            <input value={form.contact_name} onChange={(e) => update("contact_name", e.target.value)} style={inputStyle} />
          </Field>
          <Field label="Contact Email">
            <input type="email" value={form.contact_email} onChange={(e) => update("contact_email", e.target.value)} style={inputStyle} />
          </Field>
        </Row>
        <Field label="Contact Phone">
          <input value={form.contact_phone} onChange={(e) => update("contact_phone", e.target.value)} style={inputStyle} />
        </Field>

        <label style={{ display: "flex", alignItems: "center", gap: 9, padding: "10px 12px", border: `1px solid ${C.border}`, borderRadius: 8, fontSize: 13, cursor: "pointer", marginTop: 6, marginBottom: 20, background: form.is_active ? "#F3F8F5" : "#fff" }}>
          <input type="checkbox" checked={form.is_active} onChange={(e) => setForm((f) => ({ ...f, is_active: e.target.checked }))} />
          <span>Go live immediately</span>
          <span style={{ color: C.gray, fontSize: 11.5 }}>(leave unchecked to save as draft)</span>
        </label>

        <div style={{ display: "flex", gap: 8 }}>
          <button type="button" onClick={onClose} style={{ flex: 1, background: C.grayLight, border: "none", borderRadius: 8, padding: "10px 0", fontSize: 13, cursor: "pointer" }}>Cancel</button>
          <button type="submit" disabled={busy} style={{ flex: 1, background: C.green, color: "#fff", border: "none", borderRadius: 8, padding: "10px 0", fontSize: 13, fontWeight: 600, cursor: "pointer" }}>{busy ? "Saving…" : "Save District"}</button>
        </div>
      </form>
    </div>
  );
}

function Row({ children }) {
  return <div style={{ display: "flex", gap: 10 }}>{React.Children.map(children, (c) => <div style={{ flex: 1 }}>{c}</div>)}</div>;
}

function Field({ label, required, children }) {
  return (
    <div style={{ marginBottom: 12 }}>
      <label style={{ display: "block", fontSize: 11.5, fontWeight: 600, color: C.gray, marginBottom: 5, textTransform: "uppercase" }}>
        {label}{required && <span style={{ color: C.danger }}> *</span>}
      </label>
      {children}
    </div>
  );
}

const inputStyle = { width: "100%", boxSizing: "border-box", padding: "9px 12px", border: "1px solid #e4e7ed", borderRadius: 8, fontSize: 13 };
